import {
  RESOLUTION_RATE_PLACEHOLDER,
  PEAK_RESOLUTION_RATE_PLACEHOLDER,
  RESOLUTION_RATE_MONTHLY_IMPROVEMENT_PLACEHOLDER,
  FIN_SUPPORTED_LANGUAGES_PLACEHOLDER,
  INTERCOM_CUSTOMERS_PLACEHOLDER,
  FIN_CUSTOMERS_PLACEHOLDER,
  WEEKLY_RESOLUTIONS_PLACEHOLDER,
} from "./schema/constants/character-limits";
import type { DrlpGlobalStats } from "./queries/global-stats/query";

/** Map of placeholder string (e.g. "{{resolutionRate}}") to its replacement value. */
export type TokenMap = Record<string, string | null | undefined>;

/**
 * Builds the token map for DRLP rendering from the shared Global Stats document.
 * Placeholders whose value is missing are dropped, so unresolved tokens are left
 * untouched rather than replaced with an empty string.
 */
export function buildDrlpTokenMap(
  globalStats: DrlpGlobalStats | null | undefined,
): TokenMap {
  return {
    [RESOLUTION_RATE_PLACEHOLDER]: globalStats?.averageResolutionRate,
    [PEAK_RESOLUTION_RATE_PLACEHOLDER]: globalStats?.peakResolutionRate,
    [RESOLUTION_RATE_MONTHLY_IMPROVEMENT_PLACEHOLDER]:
      globalStats?.resolutionRateMonthlyImprovement,
    [FIN_SUPPORTED_LANGUAGES_PLACEHOLDER]: globalStats?.finSupportedLanguages,
    [INTERCOM_CUSTOMERS_PLACEHOLDER]: globalStats?.intercomCustomers,
    [FIN_CUSTOMERS_PLACEHOLDER]: globalStats?.finCustomers,
    [WEEKLY_RESOLUTIONS_PLACEHOLDER]: globalStats?.weeklyResolutions,
  };
}

/** True when `node` is a Portable Text span carrying a string `text` leaf. */
function isSpan(
  node: unknown,
): node is { _type: "span"; text: string; marks?: string[] } {
  return (
    node !== null &&
    typeof node === "object" &&
    (node as { _type?: unknown })._type === "span" &&
    typeof (node as { text?: unknown }).text === "string"
  );
}

/** Order-sensitive equality for two Portable Text `marks` arrays (treats undefined as empty). */
function marksEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const x = a ?? [];
  const y = b ?? [];
  if (x.length !== y.length) return false;
  return x.every((mark, i) => mark === y[i]);
}

/**
 * Coalesces adjacent spans that share identical marks into one span,
 * concatenating their `text`. The Sanity editor routinely fragments a single
 * styled run into multiple spans (on paste, mark edits, or mid-word edits), so
 * a placeholder typed as one word — `{{resolutionRate}}` — can land split
 * across spans (`"{{resolution"` + `"Rate}}"`). Without this, the per-leaf
 * replacement never sees the full placeholder and the token renders literally.
 *
 * Spans with differing marks and non-span children act as boundaries and are
 * left untouched, so a token deliberately split across a mark boundary stays
 * unresolved rather than being silently restyled. Non-mutating: merged spans
 * are new objects; unmerged children keep their original reference.
 */
function coalesceSpans(children: unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const child of children) {
    const prev = out[out.length - 1];
    if (isSpan(child) && isSpan(prev) && marksEqual(prev.marks, child.marks)) {
      out[out.length - 1] = { ...prev, text: prev.text + child.text };
    } else {
      out.push(child);
    }
  }
  return out;
}

/**
 * Recursively replaces token placeholders in every string leaf of `data`,
 * including Portable Text spans (whose `text` is a plain string leaf). Adjacent
 * same-mark spans are coalesced first so a placeholder fragmented across spans
 * still resolves (see `coalesceSpans`). Returns a new object; the input is not
 * mutated. Runs on already-fetched Sanity data, so it requires no dynamic APIs
 * and keeps pages statically rendered.
 */
export function applyTokens<T>(data: T, tokens: TokenMap): T {
  const entries = Object.entries(tokens).filter(
    (entry): entry is [string, string] =>
      typeof entry[1] === "string" && entry[1].length > 0,
  );
  if (entries.length === 0) return data;

  const replaceInString = (input: string): string => {
    let output = input;
    for (const [placeholder, value] of entries) {
      if (output.includes(placeholder)) {
        output = output.split(placeholder).join(value);
      }
    }
    return output;
  };

  const walk = (node: unknown): unknown => {
    if (typeof node === "string") return replaceInString(node);
    if (Array.isArray(node)) return node.map(walk);
    if (node !== null && typeof node === "object") {
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(node)) {
        result[key] =
          key === "children" && Array.isArray(val) && val.some(isSpan)
            ? walk(coalesceSpans(val))
            : walk(val);
      }
      return result;
    }
    return node;
  };

  return walk(data) as T;
}
